"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import EditableText from "../../../core/EditableText";
import { LocationContent, buildFullAddress, generateMapEmbedUrl } from "../types";
import {
  PiMapPin,
  PiPhone,
  PiEnvelope,
  PiClock,
  PiPaperPlaneTilt,
  PiLink,
  PiGlobe,
} from "react-icons/pi";

// ============================================================
// VARIANT 3: SIDEBAR LOCATION
// Contact info sidebar with map (like InfoSection pattern)
// ============================================================

interface SidebarLocationProps extends Omit<SectionContentProps, "content"> {
  content: LocationContent;
}

export default function SidebarLocation({ content: propContent, onContentChange, isSelected }: SidebarLocationProps) {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const fullAddress = buildFullAddress(content);
  const mapEmbedUrl = content.mapEmbedUrl || generateMapEmbedUrl(fullAddress, content.mapProvider || "google");

  const handleMapUrlEdit = () => {
    const url = prompt("Enter Google Maps Embed URL:", mapEmbedUrl);
    if (url) onContentChange?.({ ...content, mapEmbedUrl: url });
  };

  return (
    <section className="location-section-sidebar" aria-label="Contact and location information">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium mb-4">
            <PiMapPin className="w-4 h-4" />
            <EditableText
              value={content.subtitle || "Find Us"}
              onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
              as="span"
              placeholder="Subtitle"
            />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            <EditableText
              value={content.title || "Contact & Location"}
              onChange={(v) => onContentChange?.({ ...content, title: v })}
              as="span"
              placeholder="Title"
            />
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            {/* Address Card */}
            <div className="bg-slate-50 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <PiMapPin className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Visit Us</h3>
              </div>
              {fullAddress ? (
                <p className="text-slate-600 leading-relaxed text-sm">
                  <EditableText
                    value={fullAddress}
                    onChange={(v) => {
                      const parts = v.split(",").map((s) => s.trim());
                      onContentChange?.({
                        ...content,
                        address: parts[0] || "",
                        city: parts[1] || "",
                        state: parts[2] || "",
                        postalCode: parts[3] || "",
                        country: parts[4] || "",
                      });
                    }}
                    as="span"
                    placeholder="Full address"
                  />
                </p>
              ) : (
                <p className="text-slate-400 text-sm">Add address in settings</p>
              )}
            </div>

            {/* Phone Card */}
            {content.showPhone !== false && (
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <PiPhone className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Call Us</h3>
                </div>
                <a
                  href={content.phone ? `tel:${content.phone}` : "#"}
                  className="text-slate-600 hover:text-blue-600 transition-colors text-sm"
                >
                  <EditableText
                    value={content.phone || "Add phone number"}
                    onChange={(v) => onContentChange?.({ ...content, phone: v })}
                    as="span"
                    placeholder="Phone"
                  />
                </a>
                <p className="text-xs text-slate-400 mt-1">Mon-Fri, 9am-6pm</p>
              </div>
            )}

            {/* Email Card */}
            {content.showEmail !== false && (
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <PiEnvelope className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Email Us</h3>
                </div>
                <a
                  href={content.email ? `mailto:${content.email}` : "#"}
                  className="text-slate-600 hover:text-blue-600 transition-colors text-sm break-all"
                >
                  <EditableText
                    value={content.email || "Add email address"}
                    onChange={(v) => onContentChange?.({ ...content, email: v })}
                    as="span"
                    placeholder="Email"
                  />
                </a>
              </div>
            )}

            {/* Website Card */}
            {content.website && (
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <PiGlobe className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Website</h3>
                </div>
                <a
                  href={content.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-blue-600 transition-colors text-sm"
                >
                  <EditableText
                    value={content.website}
                    onChange={(v) => onContentChange?.({ ...content, website: v })}
                    as="span"
                    placeholder="Website"
                  />
                </a>
              </div>
            )}

            {/* Hours Card */}
            {content.showHours && content.hours && content.hours.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <PiClock className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900">Hours</h3>
                </div>
                <div className="space-y-2">
                  {content.hours.slice(0, 5).map((hour, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{hour.day}</span>
                      <span
                        className={
                          hour.isClosed
                            ? "text-red-500"
                            : "text-slate-900 font-medium"
                        }
                      >
                        {hour.isClosed ? "Closed" : `${hour.open} - ${hour.close}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Map + Additional Info */}
          <div className="lg:col-span-2">
            {/* Map */}
            <div className="bg-slate-100 rounded-2xl overflow-hidden h-[350px] md:h-[400px] mb-6 relative">
              {fullAddress || content.mapEmbedUrl ? (
                <>
                  <iframe
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0, borderRadius: "1rem" }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Map showing location"
                    aria-label="Interactive map"
                  />
                  {isSelected && (
                    <button
                      onClick={handleMapUrlEdit}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-1"
                    >
                      <PiLink className="w-3 h-3" />
                      Edit URL
                    </button>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-slate-200 rounded-2xl">
                  <PiMapPin className="w-10 h-10 text-slate-400 mb-3" />
                  <p className="text-slate-500 text-sm">Add address to show map</p>
                </div>
              )}
            </div>

            {/* Description */}
            {content.description && (
              <div className="prose prose-slate max-w-none mb-6">
                <p className="text-slate-600 leading-relaxed">
                  <EditableText
                    value={content.description}
                    onChange={(v) => onContentChange?.({ ...content, description: v })}
                    as="span"
                    placeholder="Description about your location..."
                  />
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-4">
              {content.ctaText && (
                <button
                  onClick={() => content.ctaLink && window.open(content.ctaLink, "_blank")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <PiPaperPlaneTilt className="w-5 h-5" />
                  <EditableText
                    value={content.ctaText}
                    onChange={(v) => onContentChange?.({ ...content, ctaText: v })}
                    as="span"
                    placeholder="Button text"
                  />
                </button>
              )}

              {/* Get Directions Button */}
              {content.showGetDirections !== false && (fullAddress || content.mapEmbedUrl) && (
                <button
                  onClick={() => {
                    const destination = encodeURIComponent(fullAddress);
                    window.open(`https://www.google.com/maps/dir/?destination=${destination}`, "_blank");
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <PiMapPin className="w-5 h-5" />
                  Get Directions
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
